import { LitElement, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { Keys } from '../../../types/common/types';
import { CollapsiblePanelEvents } from '../types';

/**
 * @summary The collapsible panel component can be used to optimize large content
 * @slot panel-header - which is always visible
 * @slot This is a default/unnamed slot. It can be displayed or hidden by clicking the header. It can be any type of HTML elements and can be set in the component via inner content (inserted between the '<gz-collapsible-panel>' tags)
 * @cssproperty --panel-header-padding
 * @cssproperty --panel-header-font
 * @cssproperty --panel-header-font-size
 * @cssproperty --panel-content-padding
 * @cssproperty --panel-content-font
 * @cssproperty --panel-content-font-size
 * @tag gz-collapsible-panel
 * @event {CustomEvent} toggle - fires when the panel is expanded/collapsed. The event detail contains the value of 'isOpen' property
 * @event {CustomEvent} panelHeaderClick - fires when the panel header is clicked, if 'hideToggle' is set to TRUE. This event can be used to implement custom behavior on the panel
 */
@customElement('gz-collapsible-panel')
export class GzCollapsiblePanelWebComponent extends LitElement {
  /**
   * Sets the expanded/collapsed state of the component
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  isOpen = false;

  /**
   * Whether the toggle indicator should be hidden. When this property is true, the 'toggle' event will not be fired.
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  hideToggle = false;

  /**
   * Different event for slot element click, if hideToggle is true the slot click is ignored
   */
  @property({ type: Boolean, attribute: true, reflect: true })
  headerSlotEvent = false;

  private readonly contentId = 'gz-collapsible-content';

  static get styles() {
    return styles;
  }

  render() {
    return html`
      <div class="panel">
        <div
          part="panel-header"
          class="panel--header"
          @click="${this.onPanelHeaderClick}"
          role="button"
          tabindex="0"
          aria-expanded="${this.isOpen ? 'true' : 'false'}"
          aria-controls="${this.contentId}"
          @keydown="${this.onKeydown}"
        >
          <span class="header-slot" part="header-slot">
            <slot name="panel-header"></slot>
          </span>
          <gz-icon
            class="panel--toggle-icon"
            name="${this.isOpen ? 'ds-icon-arrow_up_collapse' : 'ds-icon-arrow_down_expand'}"
            size="24"
            color="secondary"
          ></gz-icon>
        </div>
        <div class="panel--expander">
          <div class="panel--content" id="${this.contentId}">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }

  private onPanelHeaderClick(e: Event): void {
    const path = e.composedPath();
    const composedPathContainsElement = path.some((el: HTMLElement) => el.localName === 'slot');

    if (this.hideToggle) {
      this.dispatchEvent(new CustomEvent(CollapsiblePanelEvents.PANEL_HEADER_CLICK, { bubbles: true, composed: true }));
      return;
    }

    if (this.headerSlotEvent && composedPathContainsElement) {
      this.dispatchEvent(new CustomEvent(CollapsiblePanelEvents.SLOT_CLICK, { bubbles: true, composed: true }));
      return;
    }

    this.togglePanel();
  }

  private togglePanel(): void {
    this.isOpen = !this.isOpen;
    this.dispatchEvent(
      new CustomEvent(CollapsiblePanelEvents.TOGGLE, { bubbles: true, composed: true, detail: { isOpen: this.isOpen } })
    );
  }

  private onKeydown(e: KeyboardEvent): void {
    if (e.key === Keys.Enter || e.key === ' ') {
      e.preventDefault(); // Prevent the default action to stop scrolling when space is pressed
      this.togglePanel();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-collapsible-panel': GzCollapsiblePanelWebComponent;
  }
}
